import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { FunctionsService } from '../shared/functions.service';
import { FirestoreService } from './firestore.service';
import { HudlEvent, HudlGroup, HudlMember, Team } from './types';

export interface HudlScheduleEntry {
  id: string;
  name: string;
  isPractice: boolean;
  time: Date;
  seasonId: string;
  opponentId: string;
}

interface HudlScheduleEntryResponse {
  id: string;
  name: string;
  isPractice: Boolean;
  time: string;
}

export interface HudlMembersAndGroups {
  members: HudlMember[];
  groups: HudlGroup[];
}

export interface HudlData {
  schedule: HudlScheduleEntry[];
  membersAndGroups: HudlMembersAndGroups;
}

export enum HudlCategory {
  Game = 4,
  Practice = 5,
  Scout = 6
}

export const HudlCategories = [
  {value: HudlCategory.Game, displayValue: 'Game'},
  {value: HudlCategory.Practice, displayValue: 'Practice'},
  {value: HudlCategory.Scout, displayValue: 'Scout'}
]

export interface HudlShareEntry {
  displayValue: string;
  value: string;
}

export interface HudlShareList {
  members: HudlShareEntry[];
  groups: HudlShareEntry[];
  list: HudlShareEntry[];
}

@Injectable({ providedIn: 'root' })
export class HudlDataService {
  constructor(private fns: FunctionsService, private db: FirestoreService) { }

  async refreshData(teamId: string, types: string[]) {
    try{
      await this.fns.callFunction('refreshHudlDataOnDemand', {teamId: teamId, types: types});
    }
    catch(err){
      throw(err);
    }
  }

  loadEvents(teamPath: string): Observable<HudlScheduleEntry[]> {
    return this.db.list<HudlEvent>('hudlEvents', null, teamPath).pipe(
      map((events) => (events || [])
        .map((e) => ({
          id: e.id,
          name: e.name,
          isPractice: e.isPractice,
          time: e.time,
          seasonId: e.seasonId,
          opponentId: e.opponentId,
        } as HudlScheduleEntry))
        .sort((a, b) => a.time.valueOf() - b.time.valueOf())
      )
    );
  }

  loadShareList(teamPath: string): Observable<HudlShareList> {
    const members$ = this.db.list<HudlMember>('hudlMembers', null, teamPath).pipe(
      map((data) => (data || [])
        .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
        .map((m) => ({ displayValue: `${m.firstName} ${m.lastName}`, value: `member,${m.id}` }))
      )
    );
    const groups$ = this.db.list<HudlGroup>('hudlGroups', null, teamPath).pipe(
      map((data) => (data || [])
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((g) => ({ displayValue: `${g.name} (${g.totalCount})`, value: `group,${g.id}` }))
      )
    );
    return combineLatest([members$, groups$]).pipe(
      map(([members, groups]) => ({ members, groups, list: [...groups, ...members] }))
    );
  }

  computeDefaultShare(team: Team | null, user: any, list: HudlShareEntry[]): string[] {
    const defaults: string[] = [];
    if (user?.hudlUserId) {
      const me = list.find((s) => s.value === `member,${user.hudlUserId}`);
      if (me) {
        defaults.push(me.value);
      }
    }
    if (team && Array.isArray(team.defaultHudlShare)) {
      for (const entry of team.defaultHudlShare) {
        if (list.find((s) => s.value === entry)) {
          defaults.push(entry);
        }
      }
    }
    return defaults;
  }

  pickClosestEvent(events: HudlScheduleEntry[], practiceLookupDays: number, gameLookupDays: number): HudlScheduleEntry | null {
    if (events.length === 0) {
      return null;
    }
    const now = new Date();
    let closest = events[0];
    for (let i = 0; i < events.length; i++) {
      closest = events[i];
      const lookupDays = closest.isPractice ? practiceLookupDays : gameLookupDays;
      const isFuture = closest.time > now;
      const nextIsTooFar = i + 1 < events.length && events[i + 1].time.valueOf() - now.valueOf() > lookupDays * 24 * 60 * 60 * 1000;
      if (isFuture || nextIsTooFar) {
        break;
      }
    }
    return closest;
  }
}
